import { mix } from '$ui/Flo/util';
import React from 'react';
import styled, { css } from 'styled-components';
import { Status } from './Parts';
import { PMSEvent as PMSEventType } from '$types/timeline-v2';
import { Hue, Shade } from '$ui/Flo/types';
import * as Parts from '$ui/Timeline/Parts';
import { Icon } from '$ui/Flo/Icon';
import DentallySvg from '$resources/dentally.svg';

interface PmsProps {
    pmsEvent: PMSEventType;
    patientName: string;
    representative: {
        photo: string | null;
        name: string | null;
    };
    practiceName: string;
    hue?: Hue;
    shade?: Shade;
}

export const PMSEvent = (props: PmsProps) => {
    const { pmsEvent } = props;

    const hasNote = !!pmsEvent.note;
    const getIcon = (icon: string) => {
        switch (icon) {
            case 'attended':
                return 'CheckDouble';
            case 'booked':
                return 'Check';
            case 'cancelled':
                return 'X';
            default:
                return 'Check';
        }
    }

    const getIconColor = (color: string) => {
        switch (color) {
            case 'attended':
            case 'booked':
                return 'green';
            case 'cancelled':
                return 'red';
            default:
                return 'grey';
        }
    }

    return (
        <Container>
            <Parts.Avatar
                stroke={pmsEvent.pms_type.name !== 'cancelled' ? 'success-400' : 'error-500'}
                color={pmsEvent.pms_type.name !== 'cancelled' ? 'success-050' : 'error-050'}
                inbound={false}
                {...props}
            />
            <Holder>
                <Box
                    type={pmsEvent.pms_type.name}
                >
                    <Headline>
                        <IconWrapper>
                            <Icon
                                icon={getIcon(pmsEvent.pms_type.name)}
                                hue={getIconColor(pmsEvent.pms_type.name)}
                            />
                        </IconWrapper>
                        {pmsEvent.headline}
                    </Headline>
                    <Desc>{pmsEvent.description}</Desc>
                </Box>
                <Info>
                    {pmsEvent.pms_type.source === 'dentally' && <DentallyLogo />}
                    <NoteAndStatus>
                        {hasNote && (
                            <Note hasNote>
                                {pmsEvent.note}
                                <NoteFooter>
                                    <Icon icon="FileText" size={1.5} /> Note
                                </NoteFooter>
                            </Note>
                        )}
                        <StatusWrapper>
                            <Status
                                date={pmsEvent.datetime}
                                disableIndicator
                            />
                        </StatusWrapper>
                    </NoteAndStatus>
                </Info>
            </Holder>
        </Container>
    );
};

const Container = styled.div`
    max-width: ${mix.unit({ size: 52 })};
    min-width: ${mix.unit({ size: 52 })};
    margin-left: auto;
    display: flex;
    flex-direction: row-reverse;
    width: max-content;
    ${mix.gap({ size: 2 })};
`;

const Holder = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${mix.unit({ size: 0.5 })};
`;

const Box = styled.div<{ type: string}>`
    ${mix.color({ profile: 'body' })};
    ${mix.type({ level: 'body2' })};
    ${mix.padding({ padding: [2] })};
    display: inline-flex;
    flex-direction: column;
    ${mix.gap({ size: 1 })};
    ${({ type }) => {
        switch (type) {
            case 'attended':
            case 'booked':
                return (
                    css`
                        background: rgba(0, 255, 0, 0.075);
                        border: 1px solid hsla(130, 60%, 70%, 1);
                    `
                )
            case 'cancelled':
                return (
                    css`
                        background: rgba(255, 0, 0, 0.075);
                        border: 1px solid hsla(0, 60%, 70%, 1);
                    `
                )
            default:
                return (
                    css`
                        background: rgba(0, 0, 0, 0.1);
                        border: 1px solid hsla(0, 0%, 50%, 1);
                    `
                )
        }
    }};
    border-radius: 6px;
    width: 100%;
`;

const Headline = styled.div`
    font-size: 16px;
    font-weight: 500;
    display: flex;
    flex-direction: row;
    ${mix.color({ profile: 'title' })};
    align-items: center;
`;

const Desc = styled.div`
    ${mix.type({ level: 'body2' })};
    display: flex;
`;

const Info = styled.div`
    display: flex;
    justify-content: space-between;
    ${mix.gap({ size: 2 })}
`;

const Note = styled.div<{ hasNote: boolean }>`
    ${mix.color({ profile: 'body' })}
    ${mix.type({ level: 'body2' })};
    display: flex;
    flex-direction: column;
    ${mix.bg({ hue: 'yellow', shade: '9' })};
    ${mix.padding({ padding: [1, 2] })};
    border-radius: ${ hasNote  => hasNote ? '0px 0px 6px 6px' : '6px'};
    ${mix.gap({ size: 0.5 })}
`;

const NoteFooter = styled.div`
    ${mix.color({ profile: 'body' })};
    ${mix.type({ level: 'small' })};
    display: flex;
    align-items: center;
    width: 100%;
    ${mix.gap({ size: 0.5 })}
`;

const NoteAndStatus = styled.div`
    display: flex;
    flex-direction: column;
    ${mix.gap({ size: 0.5 })}
`;

const StatusWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const IconWrapper = styled.div`
    margin-right: 8px;
    height: 18px;
    width: 18px;
`;

const DentallyLogo = styled(DentallySvg)`
    ${mix.height({ size: 1.5 })}
    width: 40px;
`;


